import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CapabilityGate } from '../../../../components/CapabilityGate';
import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useCapacitySignals, useWaitlistSignals } from '../../../../hooks/use-scheduling';
import { useAuthStore } from '../../../../stores/auth-store';
import { relativeTime } from '../../../conversations/format';
import { SendDirectMessageModal } from '../../../direct-messages/SendDirectMessageModal';
import { Cap } from '../../capability-matrix';
import {
  AttendantDrawerStub,
  BondActivityCard,
  KpiRowSupervisor,
  PageHead,
  PriorityQueueView,
  SequenceRiskCard,
  SupervisionAlertsColumn,
  SupervisorAttendantCard,
} from '../../components';
import { AssignConversationModal } from '../../components/modals/AssignConversationModal';
import { useSupervisorDashboard } from '../../hooks/use-supervisor-dashboard';
import { useSupervisorDashboardWsSync } from '../../hooks/use-supervisor-dashboard-ws-sync';
import { useRefreshDashboard } from '../../hooks/use-supervisor-widgets';
import { isScreenReady } from '../../screens-ready';

import { adaptCapacitySignals, adaptWaitlistSignals } from './agenda-signals-adapter';
import { AgendaSignalsSection } from './AgendaSignalsSection';
import { ScenarioBanner } from './ScenarioBanner';
import { adaptDashboardToTela1 } from './tela1-adapter';

import type { SupervisorScreenId } from '../../screens-ready';
import type { AttendantVM, SupervisorVariant } from '../../view-models';

const SCREEN_ROUTE: Record<SupervisorScreenId, string> = {
  'painel-equipe': '/supervisor',
  acompanhamento: '/supervisor/acompanhamento',
  intervencao: '/supervisor/intervencao',
  'inbox-time': '/supervisor/inbox',
  'painel-sla': '/supervisor/sla',
  'dashboard-qualidade': '/supervisor/qualidade',
  'centro-alertas': '/supervisor/alertas',
  'painel-p1': '/supervisor/p1',
  'aprovacao-overrides': '/supervisor/overrides',
  insights: '/supervisor/insights',
};

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

export function PainelEquipePage(): JSX.Element {
  useSupervisorDashboardWsSync();
  const { data, isLoading, isError } = useSupervisorDashboard();
  const { data: presence } = usePresence();
  const navigate = useNavigate();
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const authUser = useAuthStore((s) => s.user);
  const authTenant = useAuthStore((s) => s.tenant);
  const refresh = useRefreshDashboard();
  // Item 352 — gate do salto "Ver no Dashboard de Qualidade" do drawer: cap canônica
  // do item de menu "Dashboard de qualidade" (reuso; NÃO inventar cap nova).
  const canVerQualidade = useCapability('quality-aggregations-read');
  // Item 353 — gate do "Drill down" da coluna Sequences p/ a ficha da Pessoa: mesma cap
  // que gateia a PessoaDetailPage / o "Ver Pessoa" do Item 323 (reuso; sem cap nova).
  const canReadPerson = useCapability('person-read');
  // Itens 354/355 + coluna B — saltos de conversa do SUPERVISOR vão para o espelho da
  // Tela 2 (/supervisor/acompanhamento/:id): a ConversationViewPage do atendente exige
  // atribuição (403 not-assigned p/ conversa de terceiro — provado no gate ao vivo
  // v3.274). Cap canônica do espelho: coattendance-shadow-observer.
  const canSeeConversation =
    useCapability('coattendance-shadow-observer') && isScreenReady('acompanhamento');
  // Item 335 — só habilita "Atribuir conversa" no card quando a cap está concedida
  // (evita botão-morto: sem a cap, o modal não abriria). Gate do fetch + ação vive
  // no próprio modal; aqui gateamos o handler para o botão refletir o estado real.
  const canAssignConversation = useCapability('conversation-assign');
  // Item 335-a (Slice E) — só habilita "Mensagem privada" no card quando a cap está
  // concedida (P19: sem a cap o botão é AUSENTE, ver SupervisorAttendantCard). Gate do
  // fetch (pessoas/times) + ação vive no próprio modal; aqui gateamos só o handler.
  const canInitiateDirect = useCapability('direct-message-initiate');
  // Sessão 5 — sinais de fila/capacidade da agenda nativa. Gateia o FETCH pela cap
  // (feedback_gatear_fetch_nao_so_render): fila por `scheduling-waitlist`, capacidade por
  // `scheduling-native`. Fora das caps nenhum GET dispara (o 403 gated não vaza em toast).
  const canWaitlistSignals = useCapability('scheduling-waitlist');
  const canCapacitySignals = useCapability('scheduling-native');
  const waitlistSignals = useWaitlistSignals(canWaitlistSignals);
  const capacitySignals = useCapacitySignals(canCapacitySignals);
  const openConversationMirror = (conversationId: string): void => {
    navigate(`/supervisor/acompanhamento/${conversationId}`);
  };
  const [drawerAtt, setDrawerAtt] = useState<AttendantVM | null>(null);
  // Item 335 — "Atribuir conversa" do card abre o modal de atribuição.
  const [assignTarget, setAssignTarget] = useState<AttendantVM | null>(null);
  // Item 335-a (Slice E) — "Mensagem privada" do card abre o modal de envio dirigido
  // com o atendente pré-selecionado e travado.
  const [directTarget, setDirectTarget] = useState<AttendantVM | null>(null);

  const go = (target: string): void => {
    const id = target as SupervisorScreenId;
    if (isScreenReady(id)) {
      navigate(SCREEN_ROUTE[id]);
    }
  };

  // Presence agora carrega o nome real do User (Engine) — nunca cair em id cru.
  const nameFor = (userId: string): string | undefined =>
    presence.find((p) => p.userId === userId)?.name ?? undefined;

  return (
    <CapabilityGate capability="supervisor-dashboard-read" fallback={<SoloUpgradeCta />}>
      {isLoading && <div aria-busy="true">Carregando painel…</div>}
      {isError && !isLoading && <div role="alert">Erro ao carregar dados do painel.</div>}
      {data !== undefined &&
        !isLoading &&
        (() => {
          const vm = adaptDashboardToTela1(data, nameFor);

          const hasOverloaded = vm.attendants.some(
            (a) => a.loadSummary !== undefined && a.loadSummary.current >= a.loadSummary.capacity,
          );

          return (
            <>
              {variant === 'regulated' && (
                <div className="banner banner--regulated">
                  <span className="banner__icon" aria-hidden="true">
                    🛡
                  </span>
                  <div className="banner__body">
                    <span className="banner__title">Operação Regulada</span>
                    <span className="banner__sub">
                      {' · '}todas as visualizações e ações são auditadas
                    </span>
                  </div>
                </div>
              )}

              <ScenarioBanner
                vm={vm}
                p1Active={data.kpis.p1Active}
                hasOverloadedAttendant={hasOverloaded}
                onNavigate={go}
              />

              <PageHead
                title="Painel da equipe"
                subtitle={`${authUser?.name ?? 'Supervisor'} · ${authTenant?.name ?? ''} · fotografia ao vivo · atualizado ${relativeTime(vm.computedAt)}`}
                actions={
                  <button
                    type="button"
                    className="btn-ghost-mini"
                    onClick={() => {
                      refresh.mutate();
                    }}
                  >
                    Refresh
                  </button>
                }
              />

              <KpiRowSupervisor kpis={vm.kpis} variant={variant} />

              <div className="tela1-body">
                {/* Coluna A — Atendentes + Bonds + Sequences */}
                <section>
                  <div className="tela1-col__title">
                    Atendentes da equipe ({vm.attendants.length})
                  </div>
                  {vm.attendants.map((att) => (
                    <SupervisorAttendantCard
                      key={att.id}
                      att={att}
                      variant={variant}
                      onOpenDrawer={setDrawerAtt}
                      {...(canAssignConversation ? { onAtribuir: setAssignTarget } : {})}
                      {...(canInitiateDirect ? { onMensagemPrivada: setDirectTarget } : {})}
                      onOpenAcompanhar={(a) => {
                        // att.id === userId (tela1-adapter: id: e.userId) → deep-link
                        // p/ o picker real das conversas in_progress do atendente (D1).
                        // O modo de observação é escolhido no espelho da Tela 2 (fluxo D3).
                        if (isScreenReady('acompanhamento')) {
                          navigate(`/supervisor/acompanhamento?attendantId=${a.id}`);
                        }
                      }}
                    />
                  ))}

                  <Cap cap="tela1.visao-bonds" variant={variant}>
                    {vm.bonds.length > 0 && (
                      <section className="tela1-col__section">
                        <div className="tela1-col__title">
                          Bonds com atividade alta hoje (top 4)
                        </div>
                        <div className="bond-grid">
                          {vm.bonds.map((b) => (
                            <BondActivityCard
                              key={b.id}
                              bond={b}
                              // Item 355 — "Ver detalhes do Bond" salta p/ o espelho da conversa
                              // mais recente do Bond (Tela 2). Habilitado só com a cap do espelho
                              // E conversationId resolvido; senão honest-defer (botão desabilitado).
                              {...(canSeeConversation && b.conversationId !== undefined
                                ? {
                                    onSeeDetail: (): void => {
                                      openConversationMirror(b.conversationId!);
                                    },
                                  }
                                : {})}
                            />
                          ))}
                        </div>
                        {vm.bondsTotal > vm.bonds.length && (
                          <button
                            type="button"
                            className="btn-ghost-mini tela1-col__see-all"
                            onClick={() => {
                              go('acompanhamento');
                            }}
                          >
                            Ver todos os {vm.bondsTotal} Bonds…
                          </button>
                        )}
                      </section>
                    )}
                  </Cap>

                  <Cap cap="tela1.visao-sequences" variant={variant}>
                    {vm.sequences.length > 0 && (
                      <section className="tela1-col__section">
                        <div className="tela1-col__title">
                          Sequences sob risco ({vm.sequences.length})
                        </div>
                        {vm.sequences.map((s) => (
                          <SequenceRiskCard
                            key={s.id}
                            seq={s}
                            // Item 353 — "Drill down" salta p/ /pessoas/<personId> (mesmo
                            // destino do "Ver Pessoa", Item 323). Card é apresentacional puro:
                            // a page navega. Habilitado só com person-read E personId resolvido;
                            // senão honest-defer (botão desabilitado no card).
                            {...(canReadPerson && s.personId !== undefined
                              ? {
                                  onDrillDown: (): void => {
                                    navigate(`/pessoas/${s.personId!}`);
                                  },
                                }
                              : {})}
                            // Item 354 — "Ver conversa atual" salta p/ o espelho da conversa
                            // atual (maior orderIndex) na Tela 2. Habilitado só com a cap do
                            // espelho E conversationId resolvido; senão honest-defer.
                            {...(canSeeConversation && s.conversationId !== undefined
                              ? {
                                  onSeeConversation: (): void => {
                                    openConversationMirror(s.conversationId!);
                                  },
                                }
                              : {})}
                          />
                        ))}
                      </section>
                    )}
                  </Cap>

                  <AgendaSignalsSection
                    waitlist={
                      canWaitlistSignals && waitlistSignals.data !== undefined
                        ? adaptWaitlistSignals(waitlistSignals.data.data)
                        : null
                    }
                    capacity={
                      canCapacitySignals && capacitySignals.data !== undefined
                        ? adaptCapacitySignals(capacitySignals.data.data)
                        : null
                    }
                    onOpenAgenda={() => {
                      navigate('/agenda');
                    }}
                  />
                </section>

                {/* Coluna B */}
                <Cap cap="tela1.coluna-b-priorizadas" variant={variant}>
                  <PriorityQueueView
                    pq={vm.priorityQueue}
                    onNavigate={go}
                    // Item canônico Brief 2B: item com conversa navega de verdade
                    // (espelho da Tela 2 — sem cap, sem affordance).
                    {...(canSeeConversation ? { onOpenConversation: openConversationMirror } : {})}
                  />
                </Cap>

                {/* Coluna C */}
                <Cap cap="tela1.coluna-c-alertas" variant={variant}>
                  <SupervisionAlertsColumn
                    alerts={vm.supervisionAlerts}
                    variant={variant}
                    onNavigate={go}
                  />
                </Cap>
              </div>

              <AttendantDrawerStub
                open={drawerAtt !== null}
                attendant={drawerAtt}
                onClose={() => {
                  setDrawerAtt(null);
                }}
                onAcompanhar={() => {
                  // Mesmo destino do "Acompanhar" do card (D1): picker das conversas
                  // in_progress do atendente na Tela 2.
                  if (drawerAtt !== null && isScreenReady('acompanhamento')) {
                    const id = drawerAtt.id;
                    setDrawerAtt(null);
                    navigate(`/supervisor/acompanhamento?attendantId=${id}`);
                  }
                }}
                // "Mensagem privada" do drawer reusa o MESMO fluxo do card (Slice E):
                // abre o SendDirectMessageModal com o atendente travado. Gateado pela cap
                // direct-message-initiate (sem a cap, botão AUSENTE — honest-defer no stub).
                {...(canInitiateDirect
                  ? {
                      onMensagemPrivada: (): void => {
                        if (drawerAtt !== null) {
                          const target = drawerAtt;
                          setDrawerAtt(null);
                          setDirectTarget(target);
                        }
                      },
                    }
                  : {})}
                // Item 352 — salto p/ o Dashboard de Qualidade filtrado por este atendente
                // (drawerAtt.id === operators[].userId na Tela 6 → seed casa). Gateado por
                // cap + tela pronta; senão honest-defer (botão desabilitado).
                {...(canVerQualidade && isScreenReady('dashboard-qualidade')
                  ? {
                      onVerQualidade: (): void => {
                        if (drawerAtt !== null) {
                          const id = drawerAtt.id;
                          setDrawerAtt(null);
                          navigate(`/supervisor/qualidade?attendantId=${id}`);
                        }
                      },
                    }
                  : {})}
              />

              {assignTarget !== null && (
                <AssignConversationModal
                  attendant={assignTarget}
                  onClose={() => {
                    setAssignTarget(null);
                  }}
                />
              )}

              {directTarget !== null && (
                <SendDirectMessageModal
                  open
                  lockedRecipient={{ userId: directTarget.id, name: directTarget.name }}
                  onClose={() => {
                    setDirectTarget(null);
                  }}
                  onSent={(threadId) => {
                    setDirectTarget(null);
                    navigate(`/mensagens/${threadId}`);
                  }}
                />
              )}
            </>
          );
        })()}
    </CapabilityGate>
  );
}

function SoloUpgradeCta(): JSX.Element {
  return (
    <div className="painel-equipe__upgrade">
      <h2>Painel da Equipe</h2>
      <p>Disponível nos pacotes Team, Pro e Regulated. Faça upgrade para monitorar sua equipe.</p>
    </div>
  );
}
